/**
 * Australian Google Places address autocomplete with manual-entry fallback.
 *
 * Uses the NEW Places API (`AutocompleteSuggestion.fetchAutocompleteSuggestions`)
 * because the legacy `google.maps.places.Autocomplete` constructor is not
 * available to Google Cloud customers created after 1 March 2025. The script
 * is loaded on demand with `loading=async` and we then `importLibrary("places")`
 * to access the new API surface.
 *
 * Country restricted to Australia via `includedRegionCodes: ["au"]`.
 *
 * If the API key is missing OR the script fails to load OR a request errors,
 * we fall back to a plain text input — the booking flow is never blocked.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

let _loadPromise = null;

/** Google place → the structured fields a booking record needs. */
function structuredPlace(place, formatted) {
  const parts = {};
  for (const c of place.addressComponents || []) {
    for (const t of c.types || []) {
      parts[t] = { long: c.longText || "", short: c.shortText || "" };
    }
  }
  const street = [parts.subpremise?.short, parts.street_number?.short]
    .filter(Boolean).join("/");
  const loc = place.location;
  return {
    property_address: formatted,
    formatted_address: formatted,
    street_address: [street, parts.route?.long].filter(Boolean).join(" ").trim(),
    suburb: (parts.locality || parts.sublocality || parts.sublocality_level_1
      || parts.administrative_area_level_2 || {}).long || "",
    state: parts.administrative_area_level_1?.short || "",
    postcode: parts.postal_code?.long || "",
    country: parts.country?.short || "AU",
    latitude: typeof loc?.lat === "function" ? loc.lat() : loc?.lat ?? null,
    longitude: typeof loc?.lng === "function" ? loc.lng() : loc?.lng ?? null,
    google_place_id: place.id || "",
    address_verified: true,
    address_source: "google_places",
  };
}

function loadGoogleMaps() {
  if (typeof window === "undefined" || !GOOGLE_KEY) {
    return Promise.reject(new Error("no-key"));
  }
  if (typeof window.google?.maps?.importLibrary === "function") {
    return Promise.resolve(window.google);
  }
  if (_loadPromise) return _loadPromise;
  _loadPromise = new Promise((resolve, reject) => {
    // With `loading=async` the script's `onload` fires before the bootstrap
    // is fully initialised — `google.maps.importLibrary` only becomes a
    // function a moment later. Poll until it is.
    const start = Date.now();
    const waitReady = () => {
      if (typeof window.google?.maps?.importLibrary === "function") {
        resolve(window.google);
        return;
      }
      if (Date.now() - start > 10000) {
        _loadPromise = null;
        reject(new Error("script-timeout"));
        return;
      }
      setTimeout(waitReady, 50);
    };
    const existing = document.querySelector("script[data-scwt-google-maps]");
    if (existing) {
      waitReady();
      return;
    }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_KEY
    )}&libraries=places&loading=async&v=weekly`;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-scwt-google-maps", "1");
    s.onload = waitReady;
    s.onerror = () => {
      _loadPromise = null;
      reject(new Error("script-error"));
    };
    document.head.appendChild(s);
  });
  return _loadPromise;
}

// Lazy-initialised once per page-load
let _placesLib = null;
let _sessionToken = null;

async function getPlacesLib() {
  if (_placesLib) return _placesLib;
  const google = await loadGoogleMaps();
  _placesLib = await google.maps.importLibrary("places");
  return _placesLib;
}

function newSessionToken() {
  // A fresh token batches typing + selection into a single billed session
  if (_placesLib?.AutocompleteSessionToken) {
    _sessionToken = new _placesLib.AutocompleteSessionToken();
  }
  return _sessionToken;
}

export function AddressAutocomplete({
  value,
  onChange,
  // Sep 2026 — optional: receive the FULL structured place (components,
  // lat/lng, place id) so a caller can stop asking for suburb/state/
  // postcode a second time. Callers that omit it behave exactly as before.
  onPlace = null,
  // Nov 2026 — optional: forwarded to the underlying input's blur. Lets a
  // caller re-derive structured fields (street/suburb/state/postcode) once
  // the customer has FINISHED typing, without fighting every keystroke the
  // way doing that split on `onChange` would (see posted-kit address review,
  // SCWT-34E6D6).
  onBlur: onBlurProp = null,
  // Told when Google degrades to manual entry, so a caller that requires a
  // verified selection can relax instead of blocking the customer.
  onAvailability = null,
  // Optional {lat, lng, radius(m)} to bias suggestions to a service area.
  biasCircle = null,
  placeholder = "123 Coastal Road, Noosa QLD",
  className = "mt-1.5 h-12 rounded-xl",
  testId = "booking-address-input",
}) {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  // Start in "ready" optimistically when a key is present; only flip to
  // "manual" if a real call throws. (A warm-up step was previously used here
  // but turned out to be fragile across hot-reloads.)
  const [autoState, setAutoState] = useState(GOOGLE_KEY ? "ready" : "manual");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [showList, setShowList] = useState(false);
  const debounceRef = useRef(null);
  const selectingRef = useRef(false);

  useEffect(() => {
    if (onAvailability) onAvailability(autoState !== "manual");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoState]);

  // Fetch suggestions (debounced) whenever the input changes
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 3 || !GOOGLE_KEY) {
      setSuggestions([]);
      return;
    }
    try {
      const places = await getPlacesLib();
      if (!_sessionToken) newSessionToken();
      const { AutocompleteSuggestion } = places;
      const req = {
        input: q,
        includedRegionCodes: ["au"],
        sessionToken: _sessionToken,
      };
      if (biasCircle) {
        req.locationBias = {
          center: { lat: biasCircle.lat, lng: biasCircle.lng },
          radius: biasCircle.radius,
        };
      }
      const { suggestions: results } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
      const list = (results || [])
        .filter((s) => s.placePrediction)
        .map((s) => ({
          id: s.placePrediction.placeId,
          mainText: s.placePrediction.mainText?.text || "",
          secondaryText: s.placePrediction.secondaryText?.text || "",
          text: s.placePrediction.text?.text || "",
          prediction: s.placePrediction,
        }));
      setSuggestions(list);
      // Nov 2026 — the debounced Places response can land AFTER the
      // customer has already blurred or pressed Escape (slow network,
      // fast typing). Only (re)show the dropdown if the input is still
      // focused, otherwise a stale suggestion list pops back open over
      // whatever the customer is now interacting with below it.
      setShowList(list.length > 0 && document.activeElement === inputRef.current);
      setActiveIdx(-1);
      // Successful call confirms autocomplete is working
      setAutoState("ready");
    } catch (e) {
      // Network / quota / permission error — degrade silently to manual entry
      // eslint-disable-next-line no-console
      console.warn("[address-autocomplete] fetch failed:", e?.message || e);
      setAutoState("manual");
      setSuggestions([]);
      setShowList(false);
    }
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 200);
  };

  const selectSuggestion = async (s) => {
    if (!s?.prediction) return;
    selectingRef.current = true;
    setShowList(false);
    try {
      const place = s.prediction.toPlace();
      const fields = onPlace
        ? ["formattedAddress", "addressComponents", "location", "id"]
        : ["formattedAddress"];
      await place.fetchFields({ fields });
      const addr = place.formattedAddress || s.text;
      onChange(addr);
      if (onPlace) onPlace(structuredPlace(place, addr));
      // Reset session token after a selection (billed session is complete)
      newSessionToken();
    } catch {
      onChange(s.text);
    } finally {
      setTimeout(() => {
        selectingRef.current = false;
      }, 100);
    }
  };

  const handleKeyDown = (e) => {
    if (!showList || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setShowList(false);
    }
  };

  const handleBlur = () => {
    // Delay so click on suggestion still fires
    setTimeout(() => {
      if (!selectingRef.current) setShowList(false);
    }, 150);
    if (onBlurProp) onBlurProp();
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        data-testid={testId}
        data-autocomplete-state={autoState}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowList(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {showList && suggestions.length > 0 && (
        <div
          data-testid="address-autocomplete-suggestions"
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.id || i}
              type="button"
              data-testid={`address-suggestion-${i}`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectingRef.current = true;
              }}
              onClick={() => selectSuggestion(s)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-100 last:border-b-0 transition-colors ${
                i === activeIdx ? "bg-cyan-50 text-cyan-900" : "hover:bg-slate-50 text-slate-800"
              }`}
            >
              <div className="font-medium">{s.mainText}</div>
              {s.secondaryText && (
                <div className="text-xs text-slate-500 mt-0.5">{s.secondaryText}</div>
              )}
            </button>
          ))}
        </div>
      )}
      {autoState === "manual" && GOOGLE_KEY && (
        <p
          data-testid="address-autocomplete-fallback"
          className="mt-1 text-[11px] text-slate-400"
        >
          Address suggestions unavailable — please type your address manually.
        </p>
      )}
    </div>
  );
}

export default AddressAutocomplete;
